'use client'

import { useState } from 'react'
import { Check, Copy, Code2 } from 'lucide-react'

/**
 * Reusable copy-paste snippet block for embeddable Radar badges.
 *
 * Shows a live SVG preview + 3 format tabs (HTML, Markdown, URL) with
 * copy-to-clipboard on each. Used on /r/[id] (single report) and
 * /tools/compare/[id] (comparison).
 */
export function EmbedSnippet({
  pageUrl,
  badgeUrl,
  altText,
}: {
  pageUrl: string
  badgeUrl: string
  altText: string
}) {
  const [format, setFormat] = useState<'html' | 'markdown' | 'url'>('markdown')
  const [copied, setCopied] = useState(false)

  const snippets = {
    html: `<a href="${pageUrl}" target="_blank" rel="noopener noreferrer"><img src="${badgeUrl}" alt="${altText}" /></a>`,
    markdown: `[![${altText}](${badgeUrl})](${pageUrl})`,
    url: badgeUrl,
  }

  const activeSnippet = snippets[format]

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(activeSnippet)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Silent fail — very rare modern browser edge case
    }
  }

  return (
    <div className='rounded-2xl border border-border bg-card p-6'>
      <div className='flex items-center gap-2 mb-4'>
        <Code2 size={16} className='text-[#008dcc]' />
        <span className='text-sm font-semibold text-foreground'>
          Embed this badge
        </span>
      </div>

      {/* Live preview */}
      <div
        className='flex items-center justify-center p-5 rounded-lg mb-4'
        style={{
          background: 'var(--muted)',
          border: '1px dashed var(--border)',
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={badgeUrl} alt={altText} />
      </div>

      {/* Format tabs */}
      <div className='flex gap-1 mb-3'>
        {(['markdown', 'html', 'url'] as const).map(f => (
          <button
            key={f}
            type='button'
            onClick={() => setFormat(f)}
            className='px-3 py-1.5 rounded-md text-xs font-semibold transition-all'
            style={{
              background: format === f ? '#008dcc20' : 'transparent',
              color: format === f ? '#008dcc' : 'var(--muted-foreground)',
              border: `1px solid ${format === f ? '#008dcc40' : 'transparent'}`,
            }}
          >
            {f === 'html'
              ? 'HTML'
              : f === 'markdown'
                ? 'Markdown'
                : 'Direct URL'}
          </button>
        ))}
      </div>

      {/* Code block with copy button */}
      <div className='relative'>
        <pre
          className='text-xs p-4 pr-12 rounded-lg overflow-x-auto leading-relaxed'
          style={{
            background: 'var(--muted)',
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          <code>{activeSnippet}</code>
        </pre>
        <button
          type='button'
          onClick={copyToClipboard}
          aria-label='Copy to clipboard'
          className='absolute top-2.5 right-2.5 p-2 rounded-md transition-colors hover:bg-background border border-border'
          style={{ background: 'var(--card)' }}
        >
          {copied ? (
            <Check size={14} style={{ color: '#22c55e' }} />
          ) : (
            <Copy size={14} className='text-muted-foreground' />
          )}
        </button>
      </div>

      <p className='text-[11px] text-muted-foreground mt-3'>
        {format === 'markdown'
          ? 'Paste into your GitHub README, docs, or any Markdown surface.'
          : format === 'html'
            ? 'Paste into any HTML page — blog, trust page, email template.'
            : 'Direct link to the SVG. Use as a custom image anywhere.'}
      </p>
    </div>
  )
}
